import React, {useEffect, useRef, useCallback} from "react"
import {useSelector, useDispatch} from "react-redux"
import {trackPlaylistItemView} from "../../../store/actions"
import {getCurrentUser} from "../../../../users/store/selectors"
import {styled} from '@material-ui/core/styles'
import RfpButton from 'rfps/components/Button'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'

const PlaylistItemViewer = ({item, applicationId, trackViews = false, isPremium = false}) => {
  // Use a ref to store watchTime so it persists between renders
  const watchTimeRef = useRef(0)
  const hasStartedPlaybackRef = useRef(false)
  // Use a ref to store the interval ID
  const trackingIdRef = useRef(null)
  const dispatchRedux = useDispatch()
  const currentUser = useSelector(getCurrentUser)

  const StyledRfpButton = styled(RfpButton)(({theme}) => ({
    backgroundColor: '#ffffff'
  }))
  
  let rfpButton = <StyledRfpButton systemId={applicationId} />  

  const stopTrackingDuration = useCallback(() => {
    if (trackingIdRef.current) {
      clearInterval(trackingIdRef.current)
      trackingIdRef.current = null
    }
  }, [])

  const startTrackingDuration = useCallback((e) => {
    if (trackingIdRef.current) {
      stopTrackingDuration()
    }

    hasStartedPlaybackRef.current = true // Set flag when playback starts
    
    trackingIdRef.current = setInterval(() => {
      watchTimeRef.current += 1
    }, 1000)
  }, [stopTrackingDuration])

  const trackView = useCallback((currentItem) => {
    if (currentItem && trackViews && currentUser) {
      dispatchRedux(trackPlaylistItemView({
        user: currentUser,
        item: currentItem,
        duration: watchTimeRef.current > 0 ? watchTimeRef.current : undefined,
        applicationId: applicationId
      }))
    }
  }, [dispatchRedux, currentUser, trackViews, applicationId])

  const visibilitychange = useCallback(() => {
    if (document.visibilityState === "visible") {
      if (watchTimeRef.current > 0) {    
        startTrackingDuration()
      }
    } else {
      if (trackingIdRef.current) {
        stopTrackingDuration()
      }
    }
  }, [startTrackingDuration, stopTrackingDuration])

  // Effect for handling item changes
  useEffect(() => {
    // Clear any existing interval and reset watch time when item changes
    stopTrackingDuration()
    watchTimeRef.current = 0
    hasStartedPlaybackRef.current = false
    
    document.addEventListener("visibilitychange", visibilitychange)
    
    // This will give us a call to track view on the unload of the item 
    // which helps to track the last item viewed when clicking away
    return () => {
      // Specify how to clean up after this effect:
      document.removeEventListener("visibilitychange", visibilitychange)
      stopTrackingDuration()
      if (item && hasStartedPlaybackRef.current) {
        trackView(item)
      }
    }
  }, [item, trackView, visibilitychange, stopTrackingDuration])

  // Additional cleanup effect to ensure interval is cleared when component unmounts
  useEffect(() => {
    return () => {
      if (trackingIdRef.current) {
        clearInterval(trackingIdRef.current)
        trackingIdRef.current = null
      }
    }
  }, [])

  const isVideo = mimeType => {
    return mimeType && mimeType.includes('video')
  }
  
  const isDocument = mimeType => {
    return mimeType && mimeType.includes('pdf')
  }

  return (
    <div className='player-outer'>
      <div className='player-inner'>
        {!item &&
          <div className='empty-playlist-item'>Click on the content to the right to view <ArrowRightAltIcon/></div>
        }
        {item && isVideo(item.file?.mimeType) &&
          <video className='video-playlist-item' controls autoPlay src={`${item.file.uri}`} onPlay={startTrackingDuration} onPause={stopTrackingDuration} />
        }
        {item && isDocument(item.file?.mimeType) &&
          <embed className='pdf-playlist-item' src={item.file.uri} onLoad={startTrackingDuration} />
        }
        {item && item.embedUrl &&
          <iframe className='iframe-playlist-item' title={item.title} src={item.embedUrl} onLoad={startTrackingDuration} />
        }
      </div>
      
      <div className="playlist-item-metadata">
        {item && 
          <>
            <div className="playlist-item-metadata-inner">
              <div className="playlist-item-title">{item.title}</div>
              <div className="playlist-item-description">{item.description}</div>
            </div>
            <div className="playlist-actions">
              {isPremium && rfpButton}
            </div>
          </>
        }
      </div>
    </div>
  )
}

export default PlaylistItemViewer
